import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Text, Button, HStack, Box, IconButton, VStack, useStyleConfig } from '@chakra-ui/react';
import { AiOutlineClose } from 'react-icons/all';
import { useCallback } from 'react';

interface ModalDateRangeSelectorProps {
  children: React.ReactNode;
  headline: string;
  information: string;
  modalOpen: Boolean;
  handleModalOpen: (value: boolean) => void;
  windowOffset: number;
  closeModal: () => void;
  handleWindowOffset: (value: number) => void;
  isTabletOrMobile?: boolean;
}

export const ModalDateRangeSelector = (props: ModalDateRangeSelectorProps): JSX.Element => {
  const {
    headline,
    information,
    modalOpen,
    handleModalOpen,
    closeModal,
    handleWindowOffset,
    isTabletOrMobile,
  } = props;

  const openModal = useCallback(() => {
    handleModalOpen(true);

    handleWindowOffset(window.scrollY);
    document.body.setAttribute(
      'style',
      `position: fixed; top: -${window.scrollY}px; left:0; right:0;`,
    );
  }, [modalOpen]);

  useEffect(() => {
    return () => {
      closeModal();
    };
  }, []);

  const overlayStylesDesktop = {
    width: '775px',
    zIndex: 102,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    right: 'unset',
    bottom: 'unset',
  };

  const menuButtonStyles = useStyleConfig('MenuButton', { variant: 'chakraSelect.MenuButton' });

  const Popup = useMemo(() => {
    return (
      <Box
        onClick={e => {
          e.stopPropagation();
        }}
        borderRadius={'md'}
        bgColor={'white'}
        position={'fixed'}
        top={0}
        left={0}
        right={0}
        bottom={0}
        overflowY={'auto'}
        overflowX={'hidden'}
        maxWidth="100vw"
        className={modalOpen ? 'open' : ''}
        css={{
          '&': {
            opacity: 0,
            height: 0,
            padding: 0,
            ...(!isTabletOrMobile ? overlayStylesDesktop : {}),
          },
          '&.open': {
            display: 'block',
            opacity: 1,
            transition: 'opacity 0.5s ease 0s',
            height: 'auto',
            padding: 'var(--chakra-space-2)',
            paddingTop: '60px',
          },
        }}>
        {isTabletOrMobile && (
          <HStack
            position={'fixed'}
            top={0}
            left={0}
            w={'100%'}
            justify={'space-between'}
            pl={2}
            bgColor={'white'}
            zIndex={2}
            shadow={'lg'}
            h={modalOpen ? '50px' : 0}>
            <Text textStyle={'category.primary'} fontSize={'20px'}>
              Beliebig
            </Text>
            <IconButton
              fontSize={'20px'}
              onClick={closeModal}
              borderRadius={'none'}
              colorScheme={'blue'}
              icon={<AiOutlineClose />}
              aria-label={'close DateRangeSelector modal'}
              variant="ghost"
              h={'100%'}
            />
          </HStack>
        )}
        {!isTabletOrMobile && (
          <IconButton
            position={'absolute'}
            right={'13px'}
            top={'3px'}
            onClick={closeModal}
            icon={<AiOutlineClose />}
            aria-label={'close DateRangeSelector modal'}
            variant="outline"
          />
        )}
        {props.children}
      </Box>
    );
  }, [modalOpen, closeModal, props.children, isTabletOrMobile, overlayStylesDesktop]);

  return (
    <Box>
      <Button __css={menuButtonStyles} w={'100%'} onClick={openModal}>
        <HStack>
          <VStack alignItems={'flex-start'} spacing={0}>
            <Text textStyle={'chakraSelect.button.headline'}>{headline}</Text>
            <Text fontSize={'sm'}>{information}</Text>
          </VStack>
        </HStack>
      </Button>
      {!isTabletOrMobile && (
        <Box
          onClick={e => {
            closeModal();
          }}
          css={{
            '&': {
              ...(modalOpen
                ? {
                    position: 'fixed',
                    left: 0,
                    top: 0,
                    width: '100vw',
                    height: '100vh',
                    background: 'var(--chakra-colors-blackAlpha-600)',
                    zIndex: 99999,
                  }
                : {}),
            },
          }}>
          {Popup}
        </Box>
      )}
      {isTabletOrMobile && <Box zIndex={modalOpen ? '-100' : 'unset'}>{Popup}</Box>}
    </Box>
  );
};
